/**
 * EndScreens.tsx —— 淘汰弹层与终局结算（PRD 模块 1「淘汰与终局」/ AC1.6、AC1.7）
 *
 * Round22 · F3：淘汰模态与终局大屏接入 DismissContract 的收起-召回（Expanded↔Collapsed）：
 *   - 收起态移除全屏遮罩 / scrim，收成顶部瘦条，露出牌桌可查看他人手牌（AC3.1/AC3.2/AC3.4）；
 *   - 顶部瘦条整条可点召回完整信息（Peek↔Recall，AC3.3）；
 *   - 终局由「页面替换者」改为「保持 Table 挂载的全屏浮层」（Table 不再被卸载，AC3.4 由 GameScreen 保证）。
 * z 层级复用既有 token（瘦条 --z-settle、终局 scrim --z-scrim），不新增 z token（设计 §4.3.3）。
 */

import type { Game, HandRecord } from '../core/game';
import { chips, signedChips } from '../state/format';
import { describeHandValue, evaluateHoleAndBoard } from '../core/evaluator';
import { Modal, PlayingCard } from './primitives';

/**
 * Round22 · F3：收起态「顶部瘦条」（Peek）。整条可点召回（≥44 高、深底浅字），不渲染任何遮罩，
 * 与结算 `.settle__peek`（贴页脚上沿）几何错位——本瘦条贴顶栏下沿。`aria-live="polite"` 播报态切换。
 */
function DismissPeekBar({
  kind,
  title,
  recallText,
  onExpand,
}: {
  kind: 'eliminated' | 'over';
  title: string;
  recallText: string;
  onExpand: () => void;
}) {
  return (
    <div className={`dismiss-peek dismiss-peek--${kind}`} role="status" aria-live="polite">
      <button type="button" className="dismiss-peek__btn" onClick={onExpand} aria-label={`${title}，${recallText}`}>
        <span className="dismiss-peek__title">{title}</span>
        <span className="dismiss-peek__recall">{recallText} ›</span>
      </button>
    </div>
  );
}

export function EliminatedDialog({
  open,
  collapsed,
  handNumber,
  peakStack,
  onObserve,
  onRestart,
  onHome,
  onCollapse,
  onExpand,
}: {
  open: boolean;
  /** Round22 · F3：收起态。true → 顶部瘦条（无遮罩）；false → 阻断式模态。 */
  collapsed: boolean;
  handNumber: number;
  peakStack: number;
  onObserve: () => void;
  onRestart: () => void;
  onHome: () => void;
  /** 收起看牌桌（Expanded → Collapsed）。 */
  onCollapse: () => void;
  /** 召回详情（Collapsed → Expanded）。 */
  onExpand: () => void;
}) {
  if (!open) return null;
  // 收起态：移除全屏遮罩，收成顶部瘦条露出牌桌（AC3.1/AC3.2/AC3.4）。
  if (collapsed) {
    return <DismissPeekBar kind="eliminated" title="你已被淘汰" recallText="查看详情" onExpand={onExpand} />;
  }
  return (
    <Modal
      open={open}
      title="你已被淘汰"
      onClose={onHome}
      closeOnMask={false}
      wide
      actions={
        <>
          <button type="button" className="btn" onClick={onHome}>
            回到首页
          </button>
          <button type="button" className="btn" onClick={onObserve}>
            观战到底
          </button>
          {/* Round22 · F3（AC3.1）：新增「收起看牌桌」出口，收起后撤遮罩、露牌桌复盘他人底牌。 */}
          <button type="button" className="btn" onClick={onCollapse}>
            收起看牌桌
          </button>
          <button type="button" className="btn btn--primary" onClick={onRestart}>
            再来一局
          </button>
        </>
      }
    >
      <p>
        筹码已归零，本局你的旅程停在第 <b className="num">{handNumber}</b> 手，峰值筹码{' '}
        <b className="num">{chips(peakStack)}</b>。
      </p>
      <p className="muted">
        观战模式下牌桌继续进行到只剩一人；赢率计算器依赖你的底牌，观战期间不可用。
      </p>
    </Modal>
  );
}

/**
 * Round22 · F3（AC-C5 复用）：终局最后一手直读摘要。Table 已保持挂载（不再卸载），
 * 本摘要仍保留作为终局浮层展开区的「最后一手」补充信息（底牌 + 成手牌型 + 盈亏），
 * 与真桌互补而非替代。
 */
function LastHandSummary({ records, humanSeat }: { records: readonly HandRecord[]; humanSeat: number }): JSX.Element | null {
  if (!records.length) return null;
  const rec: HandRecord =
    [...records].reverse().find((r) => r.board.length >= 3) ?? records[records.length - 1];
  const board = rec.board;
  const dealEv = rec.events.find(
    (e): e is Extract<HandRecord['events'][number], { kind: 'deal' }> => e.kind === 'deal' && e.seat === humanSeat,
  );
  const hole = dealEv?.cards ?? null;
  const net = rec.net.get(humanSeat) ?? 0;
  const made =
    hole && board.length >= 3 ? describeHandValue(evaluateHoleAndBoard(hole, board)).categoryName : null;
  return (
    <section className="over__lasthand">
      <h2 className="over__sub">最后一手（第 {rec.handNumber} 手）</h2>
      <div className="felt over__felt">
        <div className="board over__board" aria-label="最后一手公共牌">
          {board.length
            ? board.map((cd, i) => <PlayingCard key={i} card={cd} size="sm" dealIndex={i} />)
            : <span className="muted">本手未见公共牌（翻牌前结束）</span>}
        </div>
        <div className="felt__seat--me over__hero">
          <span className="over__hero-label">你的牌</span>
          <div className="over__hole">
            {hole ? (
              <>
                <PlayingCard card={hole[0]} size="sm" />
                <PlayingCard card={hole[1]} size="sm" />
              </>
            ) : (
              <span className="muted">未发牌</span>
            )}
          </div>
          {made ? <span className="seat__made">{made}</span> : null}
          <span className={`over__pnl num${net > 0 ? ' pos' : net < 0 ? ' neg' : ''}`}>
            最后一手 {signedChips(net)}
          </span>
        </div>
      </div>
    </section>
  );
}

export function GameOverScreen({
  game,
  collapsed,
  handsPlayed,
  peakStack,
  humanSeat,
  records,
  onRestart,
  onHome,
  onReview,
  onCollapse,
  onExpand,
}: {
  game: Game;
  /** Round22 · F3：收起态。true → 顶部瘦条（无 scrim，露牌桌）；false → 全屏浮层（scrim 压暗牌桌）。 */
  collapsed: boolean;
  handsPlayed: number;
  peakStack: number;
  humanSeat: number;
  records: readonly HandRecord[];
  onRestart: () => void;
  onHome: () => void;
  onReview: () => void;
  /** 看牌桌（Expanded → Collapsed）。 */
  onCollapse: () => void;
  /** 查看结果（Collapsed → Expanded）。 */
  onExpand: () => void;
}) {
  // 收起态：撤 scrim、收成顶部瘦条，露出保持挂载的真实牌桌复盘各家底牌（AC3.1/AC3.2/AC3.4）。
  if (collapsed) {
    return <DismissPeekBar kind="over" title="本局结束" recallText="查看结果" onExpand={onExpand} />;
  }

  const ranking = [...game.players]
    .filter((p): p is NonNullable<typeof p> => !!p)
    .map((p) => ({ seat: p.seat, name: p.name, stack: p.stack, isHuman: p.isHuman, eliminated: p.eliminated }))
    .sort((a, b) => b.stack - a.stack);
  const me = ranking.find((r) => r.seat === humanSeat);
  const myRank = me ? ranking.indexOf(me) + 1 : ranking.length;
  const champion = ranking[0];
  const iWon = champion?.seat === humanSeat;

  // Round22 · F3：由「页面替换者」改为「保持 Table 挂载的全屏浮层」——外层 `.over--overlay`
  // 绝对定位铺满牌桌区，半透明 scrim 压暗牌桌（Expanded），Table 仍在其下挂载可复盘。
  return (
    <div className="over over--overlay" role="dialog" aria-modal="false" aria-label="本局结束">
      <div className="over__scrim" aria-hidden="true" onClick={onCollapse} />
      <div className="over__card">
        {/* AC3.1：收起看牌桌出口（复用 .overlay-close 48×48 命中盒，脱离 flex、右上角、在视口内）。 */}
        <button type="button" className="overlay-close" aria-label="收起结算，查看牌桌" onClick={onCollapse}>
          ✕
        </button>
        {/* Round23 BUG-R02：终局浮层同结算三段骨架——scroll-body(可滚) + sticky footer(主 CTA 常驻 visFrac=1)。 */}
        <div className="over__body">
          <p className="over__eyebrow">本局结束</p>
          <h1 className="over__title">{iWon ? '桌上只剩你一人' : `${champion?.name ?? '对手'}拿下了全部筹码`}</h1>
          <dl className="over__stats">
            <div>
              <dt>你的名次</dt>
              <dd className="num">
                {myRank} / {ranking.length}
              </dd>
            </div>
            <div>
              <dt>总手数</dt>
              <dd className="num">{handsPlayed}</dd>
            </div>
            <div>
              <dt>峰值筹码</dt>
              <dd className="num">{chips(peakStack)}</dd>
            </div>
            <div>
              <dt>最终筹码</dt>
              <dd className="num">{chips(me?.stack ?? 0)}</dd>
            </div>
          </dl>

          <LastHandSummary records={records} humanSeat={humanSeat} />

          <h2 className="over__sub">最终排名</h2>
          <ol className="over__rank">
            {ranking.map((r, i) => (
              <li key={r.seat} className={r.isHuman ? 'over__rank-me' : ''}>
                <span className="over__rank-no num">{i + 1}</span>
                <span className="over__rank-name">{r.name}</span>
                <span className="over__rank-stack num">{chips(r.stack)}</span>
                <span className="over__rank-tag">{r.eliminated ? '已淘汰' : i === 0 ? '冠军' : ''}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="over__footer">
          <div className="over__actions">
            {/* Round22 · F3（AC3.1）：新增「看牌桌」出口，收起后撤 scrim、露真实牌桌复盘各家底牌。 */}
            <button type="button" className="btn" onClick={onCollapse}>
              看牌桌
            </button>
            <button type="button" className="btn" onClick={onReview}>
              回看最后一手
            </button>
            <button type="button" className="btn" onClick={onHome}>
              回到首页
            </button>
            <button type="button" className="btn btn--primary" onClick={onRestart}>
              用同样的设置再来一局
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
